import type { ApiSettings, MovieSummary, WatchProgress } from './types';

const SETTINGS_KEY = 'cyber-movies:settings:v3';
const PROGRESS_KEY_PREFIX = 'cyber-movies:progress:v2';
const WATCHLIST_KEY = 'cyber-movies:watchlist:v1';
const WATCHLIST_ITEMS_KEY = 'cyber-movies:watchlist-items:v2';
const PLAYBACK_PREFERENCES_KEY = 'cyber-movies:playback-preferences:v1';

export const defaultSettings: ApiSettings = {
  apiBase: import.meta.env.VITE_DISTRIBUTION_API_URL ?? 'https://s4.cyberdash.net',
  locale: 'EN',
};

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

export const settingsStore = {
  read: () => readJson(SETTINGS_KEY, defaultSettings),
  write: (settings: ApiSettings) => writeJson(SETTINGS_KEY, settings),
};

export const progressStore = {
  list: (accountUid: string) => readProgress(accountUid),
  read: (accountUid: string, movieId: string) => readProgress(accountUid).find((item) => item.movieId === movieId),
  replace: (accountUid: string, progress: WatchProgress[]) => writeJson(progressKey(accountUid), progress.slice(0, 30)),
  write: (accountUid: string, progress: WatchProgress) => {
    const items = readProgress(accountUid).filter((item) => item.movieId !== progress.movieId);
    const completed = progress.duration > 0 && progress.currentTime / progress.duration > 0.94;
    writeJson(progressKey(accountUid), completed ? items : [progress, ...items].slice(0, 30));
  },
  remove: (accountUid: string, movieId: string) => {
    const items = readProgress(accountUid).filter((item) => item.movieId !== movieId);
    writeJson(progressKey(accountUid), items);
  },
};

function progressKey(accountUid: string): string {
  return `${PROGRESS_KEY_PREFIX}:${encodeURIComponent(accountUid)}`;
}

function readProgress(accountUid: string): WatchProgress[] {
  return readJson<WatchProgress[]>(progressKey(accountUid), [])
    .filter((item) =>
      Boolean(item.movieId) &&
      Number.isFinite(item.currentTime) &&
      Number.isFinite(item.duration) &&
      Number.isFinite(item.updatedAt),
    )
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export const watchlistStore = {
  list: () => {
    const legacy = readJson<string[]>(WATCHLIST_KEY, []);
    const itemIds = readJson<MovieSummary[]>(WATCHLIST_ITEMS_KEY, []).map((item) => item.id);
    return [...new Set([...itemIds, ...legacy])];
  },
  items: () => readJson<MovieSummary[]>(WATCHLIST_ITEMS_KEY, []),
  toggle: (movieId: string, movie: MovieSummary) => {
    const items = readJson<string[]>(WATCHLIST_KEY, []);
    const saved = readJson<MovieSummary[]>(WATCHLIST_ITEMS_KEY, []);
    const exists = items.includes(movieId) || saved.some((item) => item.id === movieId);
    const nextIds = exists ? items.filter((id) => id !== movieId) : [movieId, ...items];
    const normalized = { ...movie, id: movieId };
    const nextItems = exists
      ? saved.filter((item) => item.id !== movieId)
      : [normalized, ...saved.filter((item) => item.id !== movieId)];
    writeJson(WATCHLIST_KEY, nextIds);
    writeJson(WATCHLIST_ITEMS_KEY, nextItems);
    return { ids: [...new Set([...nextItems.map((item) => item.id), ...nextIds])], items: nextItems };
  },
};

export interface PlaybackPreferences {
  audio?: string;
  subtitles?: string;
}

export const playbackPreferencesStore = {
  read: () => readJson<PlaybackPreferences>(PLAYBACK_PREFERENCES_KEY, {}),
  write: (preferences: PlaybackPreferences) => writeJson(PLAYBACK_PREFERENCES_KEY, preferences),
};
